import { useLocation, useNavigate, useParams } from "react-router-dom";
import CardMembershipRounded from "@mui/icons-material/CardMembershipRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import { Box, List, Stack } from "@mui/material";
import { AuthContext } from "@bw/shared/hooks";
import { useContext } from "react";
import { Page } from "../../../common/components/Page";
import { DetailHeader } from "../../../common/components/DetailHeader";
import { DenseRow } from "../../../common/components/DenseRow";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { EmptyState, ErrorCard, ListSkeleton } from "../../../common/components/states";
import { useFacilityPlans } from "../hooks/useFacilityPlans";
import { useMembershipFacility } from "../hooks/useMembershipFacility";
import { useMyMemberships } from "../hooks/useMyMemberships";
import { PlanList } from "../components/PlanCard";
import { deriveSubscriptionView } from "../lib/subscriptionState";
import {
  readFacilityDiscoveryReturn,
  withFacilityDiscoveryReturn,
} from "../../../common/facilityNavigation";

/**
 * Facility membership CATALOG (`/facility/:facilityId/memberships`) — the
 * discover step, reached from the facility homepage's "Memberships" row. The
 * first plan leads as a card, the rest are rows with their action; a signed-in
 * player sees their membership at this facility underneath.
 *
 * Thin presentation: plans render in server order with honest loading / error /
 * empty states.
 */
export function FacilityMembershipsPage() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // The catalog is public; only a signed-in player has a membership to show.
  const user = useContext(AuthContext)?.user ?? null;
  const facilitySearchReturn = readFacilityDiscoveryReturn(location.search);
  const plansQ = useFacilityPlans(facilityId);
  const facilityQ = useMembershipFacility(facilityId);
  const facilityPath = withFacilityDiscoveryReturn(`/facility/${facilityId}`, facilitySearchReturn);

  let body: React.ReactNode;
  if (plansQ.isLoading) {
    body = <ListSkeleton label="Loading membership plans…" rows={3} leading="square" />;
  } else if (plansQ.isError) {
    body = (
      <ErrorCard
        title="We couldn't load membership plans. Please try again."
        onRetry={() => void plansQ.refetch()}
        action={{ label: "Back to facility", to: facilityPath }}
      />
    );
  } else if (!plansQ.data || plansQ.data.length === 0) {
    body = (
      <EmptyState
        icon={<CardMembershipRounded />}
        title="This facility doesn't offer memberships yet."
        action={{ label: "Back to facility", to: facilityPath }}
      />
    );
  } else {
    body = (
      <PlanList
        plans={plansQ.data}
        onSelect={(plan) =>
          navigate(
            withFacilityDiscoveryReturn(
              `/facility/${facilityId}/memberships/${plan.plan_id}`,
              facilitySearchReturn,
            ),
          )
        }
      />
    );
  }

  return (
    <Page narrow>
      <Stack spacing={1.5}>
        <DetailHeader
          back={{ to: facilityPath, label: "Back to facility" }}
          title="Memberships"
          subtitle={facilityQ.data?.name?.trim()}
        />
        {body}
        {user && facilityId && plansQ.data?.length ? <YourMembershipHere facilityId={facilityId} /> : null}
      </Stack>
    </Page>
  );
}

/** The player's membership at this facility, from their own subscriptions. */
function YourMembershipHere({ facilityId }: { facilityId: string }) {
  const mineQ = useMyMemberships();
  if (!mineQ.isSuccess) return null;
  const here = mineQ.data.filter((sub) => sub.facility_id === facilityId);
  const current = here.filter((sub) => deriveSubscriptionView(sub).tone !== "cancelled");
  const tile = (
    <Box
      aria-hidden
      sx={{ width: 44, height: 44, borderRadius: "8px", bgcolor: "action.hover", display: "grid", placeItems: "center", color: "text.secondary", flexShrink: 0 }}
    >
      <CheckCircleRounded fontSize="small" />
    </Box>
  );
  return (
    <Stack component="section" aria-labelledby="your-membership" spacing={0.5} sx={{ pt: 1 }}>
      <SectionHeader id="your-membership" title="Your membership" />
      <List disablePadding>
        {current.length === 0 ? (
          <DenseRow leading={tile} title="None active" subtitle="Choose a plan above to join." />
        ) : (
          current.map((sub) => {
            const view = deriveSubscriptionView(sub);
            return (
              <DenseRow
                key={sub.subscription_id}
                to={`/me/memberships/${sub.subscription_id}`}
                leading={tile}
                title={sub.plan_name ?? "Membership"}
                subtitle={[view.label, view.detail].filter(Boolean).join(" · ")}
              />
            );
          })
        )}
      </List>
    </Stack>
  );
}
